/* <co-drag-scroll> — click-and-drag scrolling on its scrollable child (e.g. a <scroll-hint>).
   Mouse only; touch/trackpad keep native scrolling.

   The grab does NOT begin on pointerdown — it starts only once the pointer moves past a small
   threshold. That way a plain click on a card/link inside is never hijacked into a grab, and the
   link navigates normally; only a real drag scrubs the scroller.

   While dragging:
   - Pointer capture (setPointerCapture) keeps pointermove/up flowing to us even when the cursor is
     over a card/link, AND prevents the browser's native link-drag ghost from eating the events.
   - scroll-snap-type is forced to 'none' so each scrollLeft write scrubs smoothly instead of snapping
     card-to-card; it's restored on release so the scroller still settles on a card.
   The click that follows a real drag is suppressed so cards/links don't fire. Editor-resilient:
   all listeners + pointer capture are torn down on disconnect. */
const CO_DRAG_THRESHOLD = 8; // px of movement before a press becomes a drag (below this stays a click)

class CoDragScroll extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('scroll-hint') || this.firstElementChild;
    if (!this.scroller) return;

    this._down = false;
    this._dragging = false;
    this._moved = false;
    this._startX = 0;
    this._startLeft = 0;
    this._pointerId = null;
    this._snapBefore = '';

    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onDragStart = this._onDragStart.bind(this);

    this.scroller.addEventListener('pointerdown', this._onDown);
    this.scroller.addEventListener('click', this._onClick, true);
    // Belt-and-suspenders: kill native link/image drag everywhere inside the scroller.
    this.scroller.addEventListener('dragstart', this._onDragStart);
    this.scroller.style.cursor = 'grab';
  }

  disconnectedCallback() {
    if (this.scroller) {
      this.scroller.removeEventListener('pointerdown', this._onDown);
      this.scroller.removeEventListener('click', this._onClick, true);
      this.scroller.removeEventListener('dragstart', this._onDragStart);
      this.scroller.removeEventListener('pointermove', this._onMove);
      this.scroller.removeEventListener('pointerup', this._onUp);
      this.scroller.removeEventListener('pointercancel', this._onUp);
      this.scroller.style.cursor = '';
    }
    if (this._pointerId !== null && this.scroller) {
      try { this.scroller.releasePointerCapture(this._pointerId); } catch (_) {}
    }
  }

  _onDragStart(e) { e.preventDefault(); }

  _onDown(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return; // touch keeps native scroll

    this._down = true;
    this._dragging = false;
    this._moved = false;
    this._startX = e.clientX;
    this._startLeft = this.scroller.scrollLeft;
    this._pointerId = e.pointerId;

    // No pointer capture / grab cursor / snap-off yet — wait until the pointer crosses the threshold
    // (in _onMove) so a plain click on a link inside still navigates.
    this.scroller.addEventListener('pointermove', this._onMove);
    this.scroller.addEventListener('pointerup', this._onUp);
    this.scroller.addEventListener('pointercancel', this._onUp);
  }

  _beginDrag() {
    this._dragging = true;
    this._moved = true;
    // Disable scroll-snap so dragging scrubs smoothly (each scrollLeft write would otherwise re-snap).
    this._snapBefore = this.scroller.style.scrollSnapType;
    this.scroller.style.scrollSnapType = 'none';
    this.scroller.style.cursor = 'grabbing';
    this.scroller.style.userSelect = 'none';
    // Capture the pointer so move/up keep flowing to us even over cards/links.
    try { this.scroller.setPointerCapture(this._pointerId); } catch (_) {}
  }

  _onMove(e) {
    if (!this._down) return;
    const dx = e.clientX - this._startX;
    if (!this._dragging) {
      if (Math.abs(dx) <= CO_DRAG_THRESHOLD) return; // still within click tolerance — don't grab yet
      this._beginDrag();
    }
    this.scroller.scrollLeft = this._startLeft - dx;
  }

  _onUp(e) {
    this._down = false;
    if (this._dragging) {
      this.scroller.style.cursor = 'grab';
      this.scroller.style.userSelect = '';
      this.scroller.style.scrollSnapType = this._snapBefore; // restore the CSS-defined snap
      if (this._pointerId !== null) {
        try { this.scroller.releasePointerCapture(this._pointerId); } catch (_) {}
      }
      this._dragging = false;
      // pointercancel fires no trailing click to suppress — clear _moved now so a stale value can't
      // swallow a later keyboard/synthetic click; pointerup keeps it set for _onClick to swallow.
      if (e && e.type === 'pointercancel') this._moved = false;
    }
    this._pointerId = null;
    this.scroller.removeEventListener('pointermove', this._onMove);
    this.scroller.removeEventListener('pointerup', this._onUp);
    this.scroller.removeEventListener('pointercancel', this._onUp);
  }

  _onClick(e) {
    if (this._moved) {
      // A drag just ended — kill the click so cards/links don't fire.
      e.preventDefault();
      e.stopPropagation();
      this._moved = false;
    }
  }
}

if (!customElements.get('co-drag-scroll')) {
  customElements.define('co-drag-scroll', CoDragScroll);
}
